import { useState, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { ROLE_LABELS, type Role } from '@rundown/policy';
import { Button, Modal } from '../../../../components';
import { cx } from '../../../../lib/cx';
import { useInviteMember, useSetAccountPassword } from '../../useSettingsMutations';
import type { Grade, Membership } from '../../types';
import {
  CredentialsCard,
  DeliveryFields,
  ErrorText,
  INPUT_CLASS,
  LABEL_CLASS,
  MustChangeToggle,
  PasswordInput,
  RolePicker,
  generatePassword,
  isClientRole,
  passwordMeetsPolicy,
} from './shared';

type SignInChoice = 'google' | 'password';

interface Done {
  readonly membership: Membership;
  readonly password: string | null;
  /** The person was added but the password step failed — they still need one. */
  readonly passwordError: string | null;
}

const SIGN_IN_CHOICES: readonly { id: SignInChoice; title: string; body: string }[] = [
  {
    id: 'password',
    title: 'Set a password',
    body: 'Give them an email and password to sign in with.',
  },
  {
    id: 'google',
    title: 'Google only',
    body: 'They sign in with Google using this email. You can add a password later.',
  },
];

/**
 * Adding someone is one decision in two parts — who they are, and how they get
 * in — so it is one dialog rather than a form row plus a hidden panel on the
 * row it creates. Noorix creates every account (there is no self sign-up), so
 * without the second part a non-Google client is added with no way in at all.
 */
export function AddPersonDialog({
  workspaceId,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invite = useInviteMember(workspaceId);
  const setPassword = useSetAccountPassword(workspaceId);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('client_requester');
  // Only asked for, and only sent, when the role is a Noorix one — see
  // DeliveryFields. A client requester has no grade and no share of the week.
  const [grade, setGrade] = useState<Grade | null>(null);
  const [billingFactorBp, setBillingFactorBp] = useState<number | null>(null);
  const [allocatedHours, setAllocatedHours] = useState(0);
  const [signIn, setSignIn] = useState<SignInChoice>('password');
  const [password, setPasswordDraft] = useState(generatePassword);
  const [mustChange, setMustChange] = useState(true);
  const [done, setDone] = useState<Done | null>(null);

  const reset = () => {
    setName('');
    setEmail('');
    setRole('client_requester');
    setGrade(null);
    setBillingFactorBp(null);
    setAllocatedHours(0);
    setSignIn('password');
    setPasswordDraft(generatePassword());
    setMustChange(true);
    setDone(null);
    invite.reset();
    setPassword.reset();
  };

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const passwordOk = signIn === 'google' || passwordMeetsPolicy(password);
  const busy = invite.isPending || setPassword.isPending;
  const canSubmit = name.trim() !== '' && emailLooksValid && passwordOk && !busy;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    let membership: Membership;
    try {
      membership = await invite.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        role,
        // Sent only for a Noorix role. Sending `grade: null` for a client
        // would be harmless but says something the form never asked.
        ...(isClientRole(role) ? {} : { grade, billingFactorBp, allocatedHours }),
      });
    } catch {
      return; // Shown from invite.error.
    }

    if (signIn === 'google') {
      setDone({ membership, password: null, passwordError: null });
      return;
    }

    try {
      await setPassword.mutateAsync({ membershipId: membership.id, input: { password, mustChange } });
      setDone({ membership, password, passwordError: null });
    } catch (error) {
      setDone({
        membership,
        password: null,
        passwordError: error instanceof Error ? error.message : 'Could not set the password.',
      });
    }
  };

  if (done !== null) {
    return (
      <Modal
        open={open}
        onOpenChange={close}
        title={`${done.membership.name} was added`}
        description={`${ROLE_LABELS[done.membership.role]} · ${done.membership.email}`}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={reset}>
              Add another
            </Button>
            <Button variant="primary" size="sm" onClick={() => close(false)}>
              Done
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {done.password !== null && (
            <>
              <p className="flex items-center gap-2 fs-13 text-ink">
                <CheckCircle2 size={16} className="text-success" />
                Their account is ready. Send them these details.
              </p>
              <CredentialsCard email={done.membership.email} password={done.password} />
            </>
          )}
          {done.password === null && done.passwordError === null && (
            <p className="flex items-start gap-2 fs-13 text-ink">
              <CheckCircle2 size={16} className="mt-px flex-none text-success" />
              They can sign in with Google using {done.membership.email}. If they aren't on Google, open them
              in the list and set a password.
            </p>
          )}
          {done.passwordError !== null && (
            <>
              <p className="fs-13 text-ink">
                They were added, but the password couldn't be set. Open them in the list to try again.
              </p>
              <ErrorText message={done.passwordError} />
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="Add a person"
      description="Creates their account and gives them a role in this workspace."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-person" variant="primary" size="sm" disabled={!canSubmit}>
            {invite.isPending ? 'Adding…' : setPassword.isPending ? 'Setting password…' : 'Add person'}
          </Button>
        </>
      }
    >
      <form id="add-person" onSubmit={(event) => void submit(event)} className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Full name</span>
            <input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Jamie Rivera"
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="jamie@client.com"
              className={INPUT_CLASS}
            />
          </label>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className={cx(LABEL_CLASS, 'mb-2')}>Role</legend>
          <RolePicker
            name="add-person-role"
            value={role}
            onChange={(next) => {
              setRole(next);
              // Switching to a client role clears what does not apply, so a
              // grade picked and then abandoned cannot be submitted invisibly.
              if (isClientRole(next)) {
                setGrade(null);
                setBillingFactorBp(null);
                setAllocatedHours(0);
              }
            }}
          />
        </fieldset>

        {!isClientRole(role) && (
          <fieldset className="flex flex-col gap-2">
            <legend className={cx(LABEL_CLASS, 'mb-2')}>Delivery</legend>
            <DeliveryFields
              grade={grade}
              billingFactorBp={billingFactorBp}
              allocatedHours={allocatedHours}
              onGrade={setGrade}
              onFactor={setBillingFactorBp}
              onAllocated={setAllocatedHours}
            />
          </fieldset>
        )}

        <fieldset className="flex flex-col gap-2.5">
          <legend className={cx(LABEL_CLASS, 'mb-2')}>How will they sign in?</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {SIGN_IN_CHOICES.map((choice) => (
              <label
                key={choice.id}
                className={cx(
                  'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors',
                  signIn === choice.id
                    ? 'border-accent bg-accent-tint-soft'
                    : 'border-line hover:border-line-stronger',
                )}
              >
                <input
                  type="radio"
                  name="add-person-sign-in"
                  checked={signIn === choice.id}
                  onChange={() => setSignIn(choice.id)}
                  className="mt-[0.1875rem] accent-accent"
                />
                <span>
                  <span className="block fs-13 font-medium text-ink">{choice.title}</span>
                  <span className="block fs-11.5 leading-snug text-muted">{choice.body}</span>
                </span>
              </label>
            ))}
          </div>

          {signIn === 'password' && (
            <div className="flex flex-col gap-3 rounded-lg bg-surface p-3">
              <PasswordInput value={password} onChange={setPasswordDraft} />
              <MustChangeToggle checked={mustChange} onChange={setMustChange} />
            </div>
          )}
        </fieldset>

        <ErrorText message={invite.error?.message} />
      </form>
    </Modal>
  );
}
